/**
 * Oil & Gas — Value Realization & ROI
 *
 * Aggregates closed incident data to compute:
 *   • Product loss volume intercepted and/or lost (unit from org settings)
 *   • Regulatory fine avoidance estimate (jurisdiction-based)
 *   • Emissions reduction (CO₂-equivalent) from early detection
 *   • Nabhos ROI: cost-of-detection vs value-of-interception
 *
 * All monetary / unit constants are read from org UoM settings
 * (Organisation Settings → Units & ROI Parameters).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, DollarSign, Leaf, FileBarChart,
  RefreshCw, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import api from '../../services/api';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClosedMission {
  id: string;
  name: string;
  status: string;
  updated_at: string;
  metadata?: {
    vertical?: string;
    corridor_name?: string;
    closure_outcome?: string;
    closure_loss_volume_bbl?: number;
    closure_repair_method?: string;
    closure_regulatory?: boolean;
    closure_sealed_at?: string;
    plume_rate_kg_hr?: number;
    step8_response_type?: string;
    [key: string]: any;
  };
}

// ─── Metric card ─────────────────────────────────────────────────────────────

function MetricCard({
  label, value, sub, icon: Icon, colour,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
  colour: string;
}) {
  return (
    <Card padding="sm">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${colour}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight mt-0.5">{value}</p>
          {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

// ─── Format helpers ───────────────────────────────────────────────────────────

function fmtUSD(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

function fmtNum(v: number, unit: string): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M ${unit}`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K ${unit}`;
  return `${v.toFixed(0)} ${unit}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ValueRealizationPage: React.FC = () => {
  const { uom } = useOrgSettings();
  const [missions, setMissions] = useState<ClosedMission[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions', { params: { mission_type: 'ogi_survey', limit: 200 } });
      const all: ClosedMission[] = res.data?.missions ?? res.data?.data?.missions ?? [];
      setMissions(
        all.filter(
          (m) => m.metadata?.vertical === 'oil_gas' && m.metadata?.closure_outcome,
        ),
      );
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load closure data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Aggregations (constants from org UoM settings) ──
  const totalIncidents     = missions.length;
  const repairedCount      = missions.filter((m) => m.metadata?.closure_outcome === 'repaired').length;
  const regulatoryCount    = missions.filter((m) => m.metadata?.closure_regulatory).length;
  const totalLossVol       = missions.reduce((s, m) => s + (m.metadata?.closure_loss_volume_bbl ?? 0), 0);
  const totalPlumeKg       = missions.reduce((s, m) => s + (m.metadata?.plume_rate_kg_hr ?? 0) * 2, 0);
  const interceptedValue   = repairedCount * 5 * uom.crude_price_per_unit;
  const fineAvoidance      = regulatoryCount * uom.fine_per_incident;
  const co2eSaved          = totalPlumeKg * uom.ch4_to_co2e_factor;
  const surveyCosts        = totalIncidents * uom.survey_cost_per_mission;
  const totalValue         = interceptedValue + fineAvoidance;
  const roiMultiple        = surveyCosts > 0 ? (totalValue / surveyCosts).toFixed(1) : '—';

  // Outcome breakdown
  const OUTCOME_LABELS: Record<string, string> = {
    repaired:    'Repaired',
    shut_in:     'Shut-in',
    false_alarm: 'False Alarm',
    monitored:   'Monitoring Only',
  };
  const outcomeBreakdown = missions.reduce<Record<string, number>>((acc, m) => {
    const o = m.metadata?.closure_outcome ?? 'unknown';
    acc[o] = (acc[o] ?? 0) + 1;
    return acc;
  }, {});

  // fmt helpers use org currency
  const fmtMoney = (v: number): string => {
    const sym = uom.currency_symbol;
    if (v >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000)     return `${sym}${(v / 1_000).toFixed(0)}K`;
    return `${sym}${v.toFixed(0)}`;
  };
  const fmtNum = (v: number, unit: string): string => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M ${unit}`;
    if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K ${unit}`;
    return `${v.toFixed(0)} ${unit}`;
  };

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-green-600" /> Value Realization &amp; ROI
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Product-loss quantification, regulatory fine avoidance, and emissions-reduction ROI — derived from closed incident records
          </p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <div className="flex justify-center py-14"><Spinner size="lg" /></div>
      ) : missions.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <TrendingUp className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No closed incidents yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Close incidents in <strong>Incident Closure</strong> to generate ROI metrics
            </p>
          </div>
        </Card>
      ) : (
        <>
          {/* ── Key metrics ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              label="Intercepted Product Value"
              value={fmtMoney(interceptedValue)}
              sub={`Based on ${repairedCount} repaired incidents`}
              icon={DollarSign}
              colour="bg-green-100 dark:bg-green-900/30 text-green-600"
            />
            <MetricCard
              label="Regulatory Fine Avoidance"
              value={fmtMoney(fineAvoidance)}
              sub={`${regulatoryCount} incidents reported to regulator`}
              icon={FileBarChart}
              colour="bg-blue-100 dark:bg-blue-900/30 text-blue-600"
            />
            <MetricCard
              label="CO₂-Equivalent Prevented"
              value={fmtNum(co2eSaved, 'kg')}
              sub={`Based on GWP-100 factor: ${uom.ch4_to_co2e_factor}`}
              icon={Leaf}
              colour="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600"
            />
            <MetricCard
              label="Return on Detection"
              value={`${roiMultiple}×`}
              sub={`${fmtMoney(totalValue)} value / ${fmtMoney(surveyCosts)} survey cost`}
              icon={TrendingUp}
              colour="bg-orange-100 dark:bg-orange-900/30 text-orange-600"
            />
          </div>

          {/* ── Summary row ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Closed Incidents',    value: totalIncidents,                                colour: 'text-gray-900 dark:text-white' },
              { label: 'Total Product Loss',  value: `${totalLossVol} ${uom.volume_unit_label}`,    colour: 'text-red-700 dark:text-red-400' },
              { label: 'Repaired',            value: repairedCount,                                 colour: 'text-green-700 dark:text-green-400' },
              { label: 'Regulatory Notified', value: regulatoryCount,                               colour: 'text-blue-700 dark:text-blue-400' },
            ].map(({ label, value, colour }) => (
              <Card key={label} padding="sm">
                <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                <p className={`text-2xl font-bold mt-1 ${colour}`}>{value}</p>
              </Card>
            ))}
          </div>

          {/* ── Outcome breakdown ── */}
          <Card>
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-green-600" /> Incident Outcome Breakdown
            </p>
            <div className="flex flex-wrap gap-3">
              {Object.entries(outcomeBreakdown).map(([outcome, count]) => {
                const pct = Math.round((count / totalIncidents) * 100);
                return (
                  <div
                    key={outcome}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex-1 min-w-[140px]"
                  >
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{count}</p>
                      <p className="text-xs text-gray-400">{pct}%</p>
                    </div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {OUTCOME_LABELS[outcome] ?? outcome}
                    </p>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* ── Per-incident table ── */}
          <Card padding="none">
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Closed Incident Records ({missions.length})
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {['Incident', 'Corridor', 'Outcome', `Loss (${uom.volume_unit_label})`, `Plume Rate (${uom.mass_rate_unit_label})`, 'Regulatory', 'Closed'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {missions.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[200px] truncate">{m.name}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">{m.metadata?.corridor_name ?? '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${
                          m.metadata?.closure_outcome === 'repaired'    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                          m.metadata?.closure_outcome === 'shut_in'     ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                          m.metadata?.closure_outcome === 'false_alarm' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                          'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {OUTCOME_LABELS[m.metadata?.closure_outcome ?? ''] ?? (m.metadata?.closure_outcome ?? '—')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400">
                        {m.metadata?.closure_loss_volume_bbl != null ? m.metadata.closure_loss_volume_bbl : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400">
                        {m.metadata?.plume_rate_kg_hr != null ? `${m.metadata.plume_rate_kg_hr} ${uom.mass_rate_unit_label}` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {m.metadata?.closure_regulatory
                          ? <CheckCircle2 className="w-4 h-4 text-green-500" />
                          : <AlertTriangle className="w-4 h-4 text-gray-300 dark:text-gray-600" />}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {m.metadata?.closure_sealed_at
                          ? new Date(m.metadata.closure_sealed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── Methodology note ── */}
          <Card padding="sm">
            <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
              <strong className="text-gray-500 dark:text-gray-400">Methodology:</strong> Intercepted product value uses an average of 5 {uom.volume_unit_label} saved per early repair at {uom.currency_symbol}{uom.crude_price_per_unit}/{uom.volume_unit_label}.
              Fine avoidance uses {uom.currency_symbol}{uom.fine_per_incident.toLocaleString()} per regulatory-reported incident.
              Emissions reduction applies GWP-100 factor of {uom.ch4_to_co2e_factor} kg CO₂e per kg CH₄.
              Survey cost uses {uom.currency_symbol}{uom.survey_cost_per_mission.toLocaleString()} per OGI survey all-in.
              Update these in <strong>Organisation Settings → Units &amp; ROI Parameters</strong>.
            </p>
          </Card>
        </>
      )}
    </div>
  );
};

export default ValueRealizationPage;

// Made with Bob
